import { useEffect, useState, useCallback, useMemo, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { LottieSplatPlayer } from "./LottieSplatPlayer";
import { TomatoTripleThrowLottie } from "./TomatoTripleThrowLottie";
import { EggTripleThrowLottie } from "./EggTripleThrowLottie";
import {
  reactionTypeToThrowKind,
  reactionTypeOverlayLabel,
  getThrowVisual,
  pickStableTomatoThrowSrc,
  WET_PAINT_SPLAT_JSON,
  type ThrowSplatKind,
} from "../reactionThrowAssets";
import "./ReactionOverlay.css";

/** Inline so no parent/theme CSS can shrink the stage (matches your screen recording). */
const SPLAT_STAGE_STYLE: CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100vw",
  height: "100vh",
  minHeight: "100dvh",
  maxWidth: "100vw",
  maxHeight: "100vh",
  margin: 0,
  padding: 0,
  pointerEvents: "none",
  transform: "translateZ(0)",
  WebkitTransform: "translateZ(0)",
};

const OVERLAY_ROOT_STYLE: CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100vw",
  height: "100vh",
  minHeight: "100dvh",
  maxWidth: "100vw",
  maxHeight: "100vh",
  margin: 0,
  padding: 0,
  zIndex: 20050,
  pointerEvents: "none",
  overflow: "hidden",
  /* Own compositing layer — avoids iOS Safari painting full-screen fixed content behind MUI stacking contexts. */
  transform: "translateZ(0)",
  WebkitTransform: "translateZ(0)",
};

export interface ReactionEvent {
  eventId: string;
  fromPlayerName: string;
  reactionType: string;
}

interface Props {
  reactions: ReactionEvent[];
  onComplete: (eventId: string) => void;
}

const MAX_ACTIVE = 3;
const DURATION_MS = 2000;

const randomBetween = (min: number, max: number) =>
  Math.random() * (max - min) + min;

const FloatParticles = ({ emoji }: { emoji: string }) => {
  const particles = Array.from({ length: 6 }, (_, i) => ({
    id: i,
    left: `${randomBetween(20, 80)}%`,
    bottom: `${randomBetween(10, 30)}%`,
    delay: `${randomBetween(0, 0.4)}s`,
    size: `${randomBetween(1.4, 2.6)}rem`,
  }));

  return (
    <>
      {particles.map((p) => (
        <span
          key={p.id}
          className="reaction-float-particle"
          style={{
            left: p.left,
            bottom: p.bottom,
            animationDelay: p.delay,
            fontSize: p.size,
          }}
        >
          {emoji}
        </span>
      ))}
    </>
  );
};

function ThrowLottieReaction({
  reaction,
  kind,
  onDone,
}: {
  reaction: ReactionEvent;
  kind: ThrowSplatKind;
  onDone: () => void;
}) {
  const label = reactionTypeOverlayLabel(reaction.reactionType);
  const tomatoTripleSrc = useMemo(
    () => pickStableTomatoThrowSrc(reaction.eventId),
    [reaction.eventId]
  );
  const vis = useMemo(() => {
    if (kind === "tomato" || kind === "egg") return null;
    return getThrowVisual(kind, reaction.eventId);
  }, [kind, reaction.eventId]);

  const visEgg = useMemo(() => {
    if (kind !== "egg") return null;
    return getThrowVisual("egg", reaction.eventId);
  }, [kind, reaction.eventId]);

  if (kind === "tomato") {
    return (
      <>
        <div className="reaction-splat-stage" style={SPLAT_STAGE_STYLE}>
          <div className="reaction-splat-mass">
            <TomatoTripleThrowLottie
              src={tomatoTripleSrc}
              onComplete={onDone}
              className="reaction-lottie-splat-canvas"
            />
          </div>
        </div>
        <span className="reaction-sender-label">
          {reaction.fromPlayerName} sent {label}
        </span>
      </>
    );
  }

  if (kind === "egg" && visEgg) {
    return (
      <>
        <div className="reaction-splat-stage" style={SPLAT_STAGE_STYLE}>
          <div className="reaction-splat-mass">
            <EggTripleThrowLottie
              dedupeId={reaction.eventId}
              src={visEgg.src}
              filter={visEgg.filter}
              onComplete={onDone}
              className="reaction-lottie-splat-canvas"
            />
          </div>
        </div>
        <span className="reaction-sender-label">
          {reaction.fromPlayerName} sent {label}
        </span>
      </>
    );
  }

  if (!vis) return null;
  /** Wet-paint reuse uses the splat pop; first dedicated scenes use a full reveal. */
  const popClass =
    vis.src === WET_PAINT_SPLAT_JSON
      ? "reaction-splat-pop-inner reaction-tomato-splat-pop"
      : "reaction-splat-pop-inner reaction-scene-reveal";

  return (
    <>
      <div className="reaction-splat-stage" style={SPLAT_STAGE_STYLE}>
        <div className="reaction-splat-mass">
          <div className={popClass}>
            <LottieSplatPlayer
              src={vis.src}
              filter={vis.filter}
              onComplete={onDone}
              className="reaction-lottie-splat-canvas"
            />
          </div>
        </div>
      </div>
      <span className="reaction-sender-label">
        {reaction.fromPlayerName} sent {label}
      </span>
    </>
  );
}

const EmojiReaction = ({
  reaction,
  onDone,
}: {
  reaction: ReactionEvent;
  onDone: () => void;
}) => {
  useEffect(() => {
    const timer = setTimeout(onDone, DURATION_MS);
    return () => clearTimeout(timer);
  }, [onDone]);

  const { reactionType: emoji, fromPlayerName } = reaction;

  return (
    <>
      <span className="reaction-emoji-burst">{emoji}</span>
      <FloatParticles emoji={emoji} />
      <span className="reaction-sender-label">
        {fromPlayerName} sent {emoji}
      </span>
    </>
  );
};

const SingleReaction = ({
  reaction,
  onDone,
}: {
  reaction: ReactionEvent;
  onDone: () => void;
}) => {
  const kind = reactionTypeToThrowKind(reaction.reactionType);
  if (kind) {
    return <ThrowLottieReaction reaction={reaction} kind={kind} onDone={onDone} />;
  }
  return <EmojiReaction reaction={reaction} onDone={onDone} />;
};

export const ReactionOverlay = ({ reactions, onComplete }: Props) => {
  const [active, setActive] = useState<ReactionEvent[]>([]);

  useEffect(() => {
    if (reactions.length === 0) return;
    setActive((prev) => {
      const newItems = reactions.filter(
        (r) => !prev.some((a) => a.eventId === r.eventId)
      );
      if (newItems.length === 0) return prev;
      const combined = [...prev, ...newItems];
      return combined.slice(-MAX_ACTIVE);
    });
  }, [reactions]);

  const handleDone = useCallback(
    (eventId: string) => {
      setActive((prev) => prev.filter((r) => r.eventId !== eventId));
      onComplete(eventId);
    },
    [onComplete]
  );

  if (active.length === 0) return null;

  const tree = (
    <div className="reaction-overlay-container" style={OVERLAY_ROOT_STYLE}>
      {active.map((r) => (
        <SingleReaction
          key={r.eventId}
          reaction={r}
          onDone={() => handleDone(r.eventId)}
        />
      ))}
    </div>
  );

  if (typeof document === "undefined") return tree;

  return createPortal(tree, document.body);
};
